<template>
	<div class="link-tag-input" :class="classes">
		<link-move-container direction="top">
			<slot name="tagPrepend"></slot>
			<link-tag
				v-for="(tag,index) in tags"
				:label="tag[labelKey||'label']"
				:value="tag.value"
				:color="tagColor|| tag[colorKey||'primary']"
				:deleteable="tag.deleteable"
				:type="tag.type || tagType"
				:shape="tag.shape || tagShape"
				:dashed="tag.dashed"
				:renderFunc="renderFunc"
				:key="tag._tagKey"
				:index="index"
				:data="tag"
				@remove="_handleRemove"
				:scoped-slot-func="$scopedSlots.default"
			/>
			<input v-model="label"
				   :style="inputStyles"
				   class="link-tag-label-input"
				   key="input"
				   v-if="!notInput && !disabled"
				   @keyup.enter="_handlerConfirm"
				   @keyup.backspace="_handleBakcspace"
				   :placeholder="placeholder"
			/>
		</link-move-container>
	</div>
</template>

<script>
	import LinkTag from "./link-tag";
	import LinkMoveContainer from "../move/link-move-container";

	export default {
		name: 'link-tag-input',
		components: {
			LinkMoveContainer,
			LinkTag,
		},
		props: {
			tags: {type: Array, default: () => []},
			labelKey: {type: String, default: 'label'},
			colorKey: {type: String, default: 'primary'},

			tagColor: {type: String, default: 'info'},
			tagType: {type: String, default: 'line'},
			tagShape: {type: String, default: 'none'},

			type: {type: String, default: 'line',},
			color: {type: String, default: 'info',},
			size: {type: String, default: 'default',},
			shape: {type: String, default: 'none',},
			dashed: {type: Boolean, default: false,},
			long: {type: Boolean},
			disabled: {type: Boolean,},
			notInput: {type: Boolean,},
			renderFunc: {type: Function,},
			placeholder: {type: String},
		},
		watch: {
			tags: {
				immediate: true,
				handler(val) {
					if (!!val) {
						val.forEach((item, index) => {
							if (!item._tagKey) {
								Object.defineProperty(item, '_tagKey', {
									enumerable: false,
									value: LinkUtils.uuid()
								});
							}
						});
					}
				},
			},
			label(val) {
				if (!!val) {
					this.inputEmpty = false
					if (!!this.timer) {
						clearTimeout(this.timer)
						this.timer = null
					}
				} else {
					this.timer = setTimeout(() => {
						this.inputEmpty = true
						this.timer = null
					}, 300)
				}
			},
		},
		data() {
			return {
				label: '',
				inputEmpty: true,
				timer: null,
			};
		},
		methods: {
			_handleRemove(index) {
				if (!this.disabled) {
					const tag = this.tags.splice(index, 1);
					this.$emit('delete', tag)
				}
			},
			_handlerConfirm() {
				!!this.label && this.$emit('confirm', this.label);
				this.label = '';
			},
			_handleBakcspace() {
				if (this.inputEmpty) {
					this.$emit('backspace')
				}
			},
		},
		computed: {
			classes() {
				return [
					`link-tag-input-${this.type}`,
					`link-tag-input-color-${this.color}`,
					`link-tag-input-${this.size}`,
					`link-tag-input-shape-${this.shape}`,
					{
						'link-tag-input-dashed': !!this.dashed,
						'link-tag-input-long': !!this.long,
						'link-tag-input-disabled': !!this.disabled,
					}
				];
			},
			inputStyles() {
				let labelLength = this.label.length - 0;
				return {
					width: `${(labelLength < 8 ? 8 : labelLength + 1)}em`
				};
			},
		},
	};
</script>

<style lang="scss">
	@include themeify {
		.link-tag {
			display: inline-block;
			vertical-align: top;
			cursor: pointer;
			padding: 0 0.5em;
			box-sizing: border-box;
			user-select: none;
			/*shape*/
			@each $key, $value in $list-shape {
				&.link-tag-shape-#{$key} {
					border-radius: $value;
				}
			}
			/*fill type*/
			&.link-tag-fill {
				@include pl-colors(link-tag) {
					background-color: $value;
					border: solid $border-size $value;
					color: white;
				}
			}

			/*line type*/
			&.link-tag-line {
				@include pl-colors(link-tag) {
					border: solid $border-size $value;
					background: transparent;
					color: $color-normal-input;
				}

				&.link-tag-dashed {
					border-style: dashed;
				}
			}

			/*none type*/
			&.link-tag-none {
				@include pl-colors(link-tag) {
					border: solid $border-size transparent;
					background: transparent;
					color: $color-normal-input;
				}
			}

			/*tap style*/
			&:active {
				position: relative;
				top: 1px;
				left: 1px;
			}

			&.link-tag-disabled {
				background-color: $color-normal-disabled !important;
				color: $color-normal-disabled-text !important;;
				border-color: $color-normal-disabled !important;;
				cursor: not-allowed;
			}
		}

		.link-tag-input {
			display: inline-flex;
			align-items: center;
			vertical-align: bottom;
			box-sizing: border-box;
			padding: 0 0.25em;

			.link-tag-label-input {
				border: none;
				outline: none;
				box-sizing: border-box;
				padding: 0.25em 0.5em;
				margin-top: 0.25em;
				margin-right: 0.25em;
				font-size: inherit;
			}

			.link-tag {
				margin-bottom: 0.25em;
				margin-top: 0.25em;
				margin-right: 0.25em;
			}

			@include pl-colors(link-tag-input) {
				.link-tag-label-input {
					background-color: transparent;
				}
			}

			/*size*/
			@each $key, $value in $list-size {
				&.link-tag-input-#{$key} {
					min-height: $value;
					font-size: $value/2.5;

					.link-tag {
						padding: 2px 8px;
					}
				}
			}
			/*shape*/
			@each $key, $value in $list-shape {
				&.link-tag-input-shape-#{$key} {
					border-radius: $value;
				}
			}
			/*fill type*/
			&.link-tag-input-fill {
				@include pl-colors(link-tag-input) {
					background-color: $value;
					border: solid $border-size $value;
					color: white;

					.link-tag-label-input {
						color: white;
					}
				}
			}

			/*line type*/
			&.link-tag-input-line {
				@include pl-colors(link-tag-input) {
					border: solid $border-size $value;
					@include input-placeholder-color;
					background: transparent;
					color: $value;
				}

				&.link-tag-input-dashed {
					border-style: dashed;
				}
			}

			/*none type*/
			&.link-tag-input-none {
				@include pl-colors(link-tag-input) {
					border: solid $border-size transparent;
					@include input-placeholder-color;
					background: transparent;
					color: $value;
				}
			}

			&.link-tag-input-long {
				width: 100%;
			}

			&.link-tag-input-disabled {
				background-color: $color-normal-disabled !important;
				color: $color-normal-disabled-text !important;;
				border-color: $color-normal-disabled !important;;
				cursor: not-allowed;

				.link-tag {
					cursor: not-allowed;
				}
			}
		}
	}

</style>
